/**
 * `CreditTransactionsTable` — the REUSABLE credits-ledger table (v2 — Transaction
 * Ledger, DNKADM-0149 U2).
 *
 * Pure presentational: it takes already-parsed {@link AdminCreditTransactionRow}s and
 * a `memberScoped` flag and renders the newest-first credit-moved rows. It owns NO
 * loading/paging/filter state — the screen (and the member-detail Transactions tab in
 * DNKADM-0150) supplies the rows and wraps the states. `memberScoped` hides the
 * Contact/Actor columns (every row is the same member on the member-detail tab).
 *
 * Row rendering (spec §5): `createdAt`, contact (`contactName ?? contactId`), `delta`
 * via **`formatCredits`** (a debit is negative/red, a grant positive/green), bucket
 * (`bucketName ?? creditBucketId`), `reason` (via `ledgerReasonLabel` — the curated
 * v2 credit-reason humanizer, DNKADM-0155), the reservation reference (uuid deep link
 * or plain `reservationId`), location (`locationName`/resolver/id), `actor`, and
 * `note`. Enrichment is best-effort — a null name falls back to the id, never an
 * error.
 *
 * DNKADM-0394 U1 dropped the "Grant" column: it held `lotId`, the credit batch a
 * movement drew from, which the desk cannot act on. No column replaced it.
 *
 * **"Good through" on grant rows (DNKADM-0395 U3).** DNKAPI-0550 U3 added
 * `lotExpiresAt` to the row, so a GRANT (`delta > 0`) now carries the date its credits
 * stop being usable — the fact the desk actually needs when a member asks "how long do
 * these last?". It renders as a subordinate line under the Credits amount via the shared
 * {@link expiryLabel}, the same convention the lots view uses, rather than as a returned
 * column: it is an attribute of the granted amount, not a field of its own.
 *
 * **Only ever rendered when `lotExpiresAt` is non-null.** A null conflates three
 * different facts (bucket-level row / lot never expires / lot unresolvable — see
 * `AdminCreditTransactionRow.lotExpiresAt`), so it can only be an omission. In
 * particular `expiryLabel(null)` returns **"Never expires"**, which would be a
 * FALSEHOOD on a bucket-level row; the guard below is what keeps that string
 * unreachable from this table.
 */
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { formatCredits } from '@/utils/v2Format';
import { MemberNameLink } from '@/components/MemberNameLink';
import { expiryLabel } from '@/utils/creditExpiry';
import { useCalendarZone } from '@/location/useCalendarZone';
import { inkAlpha, resolveTokens } from '@/theme';
import type { AdminCreditTransactionRow } from '@/api/creditTransactions';
import { ReservationRefCell } from './transactionCells';
import { formatTransactionTime } from './transactionFormat';
import { ledgerReasonLabel } from '@/features/members/ledgerReasons';
import { useLocationName } from '@/location/useLocationName';

/** Colour for a credit delta: debit (negative) red, grant (positive) green, zero neutral. */
function deltaColor(delta: number, theme: Theme): string {
  const t = resolveTokens(theme);
  if (delta < 0) return t.error;
  if (delta > 0) return t.brand;
  return inkAlpha(theme, 0.7);
}

/** A signed credit amount, e.g. `+12.00` / `-5.00` (`formatCredits` already signs negatives). */
function signedCredits(delta: number): string {
  const text = formatCredits(delta);
  return delta > 0 ? `+${text}` : text;
}

export interface CreditTransactionsTableProps {
  rows: AdminCreditTransactionRow[];
  /** When true (member-detail tab, DNKADM-0150/0160) the Contact and Actor columns are hidden. */
  memberScoped?: boolean;
  /** The app-selected owned location id; used only to decide whether reservation links are safe. */
  currentLocationId?: string | null;
  /**
   * Optional id → display-name resolver supplied by the screen; without one the table resolves
   * from the location context ({@link useLocationName}).
   */
  resolveLocationName?: (id: string) => string | null;
  /** Venue IANA zone from the screen's location context; null renders in the browser zone. */
  timeZone?: string | null;
}

/**
 * The row's location name, or a dash: never the raw location id, like the Payments table
 * (DNKADM-0476 F-07). An id no resolver knows (still loading, or no longer a location) is a dash.
 */
function locationLabel(
  row: AdminCreditTransactionRow,
  resolveLocationName: (id: string) => string | null,
): string {
  if (row.locationName !== null) return row.locationName;
  if (row.locationId !== null) return resolveLocationName(row.locationId) ?? '—';
  return '—';
}

function hasReservationRef(row: AdminCreditTransactionRow): boolean {
  return row.reservationUuid !== null || row.reservationId !== null;
}

function reservationIsDifferentLocation(
  row: AdminCreditTransactionRow,
  currentLocationId: string | null | undefined,
): boolean {
  return (
    hasReservationRef(row) &&
    row.locationId !== null &&
    currentLocationId !== null &&
    currentLocationId !== undefined &&
    row.locationId !== currentLocationId
  );
}

/** The reusable, presentational credits-ledger table. */
export function CreditTransactionsTable({
  rows,
  memberScoped = false,
  currentLocationId,
  resolveLocationName,
  timeZone = null,
}: CreditTransactionsTableProps) {
  const theme = useTheme();
  // "Good through" is a lot day on the location calendar (DNKADM-0439, Operator ruling
  // 2026-10-15); `timeZone` above only formats the row's timestamp.
  const calendarZone = useCalendarZone();
  const contextLocationName = useLocationName();
  const resolveLocation = resolveLocationName ?? contextLocationName;
  return (
    <TableContainer>
      <Table aria-label="Credit transactions" size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            {!memberScoped && <TableCell>Contact</TableCell>}
            <TableCell align="right">Credits</TableCell>
            <TableCell>Bucket</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Reservation</TableCell>
            <TableCell>Location</TableCell>
            {!memberScoped && <TableCell>Actor</TableCell>}
            <TableCell>Note</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>{formatTransactionTime(row.createdAt, timeZone)}</TableCell>
              {!memberScoped && (
                <TableCell>
                  {row.contactName === null && row.contactId === null ? (
                    '—'
                  ) : (
                    <MemberNameLink name={row.contactName} contactId={row.contactId} />
                  )}
                </TableCell>
              )}
              <TableCell align="right">
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: deltaColor(row.delta, theme) }}>
                  {signedCredits(row.delta)}
                </Typography>
                {/* Grant rows only, and only with a real boundary: a null `lotExpiresAt`
                    means one of three different things, so it may only be omitted. */}
                {row.delta > 0 && row.lotExpiresAt !== null && (
                  <Typography sx={{ fontSize: 11, color: inkAlpha(theme, 0.62) }}>
                    {expiryLabel(row.lotExpiresAt, calendarZone, row.lotLastUsableDay)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{row.bucketName ?? row.creditBucketId ?? '—'}</TableCell>
              <TableCell>{row.reason !== null ? ledgerReasonLabel(row.reason) : '—'}</TableCell>
              <TableCell>
                <Stack sx={{ alignItems: 'flex-start' }}>
                  {reservationIsDifferentLocation(row, currentLocationId) ? (
                    <span>Different location</span>
                  ) : (
                    <ReservationRefCell reservationUuid={row.reservationUuid} reservationId={row.reservationId} />
                  )}
                </Stack>
              </TableCell>
              <TableCell>{locationLabel(row, resolveLocation)}</TableCell>
              {!memberScoped && <TableCell>{row.actor ?? '—'}</TableCell>}
              <TableCell>{row.note ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
